import React, { useState } from 'react';
import axios from 'axios';
import './index.css';
import { POST_FOUNDERS_CIRCLE_DATA } from '../../api/foundersCircle';
import {
    validateRequired,
    validateEmail,
    validateUrl,
    validatePhone,
    validateNumeric,
    validateInteger,
    validatePercentage,
    validateSelect,
    validateBoolean,
} from '../../utils/validators';

const steps = [
    { id: 1, label: 'Founder & company', percent: 25 },
    { id: 2, label: 'Stage & traction', percent: 50 },
    { id: 3, label: 'Preferences & trust', percent: 75 },
    { id: 4, label: 'Review & submit', percent: 100 },
];

const focusAreas = [
    'Hiring & executive leadership',
    'Fundraising & board dynamics',
    'Founder psychology & resilience',
    'Pricing & commercial motion',
    'Market expansion & GTM',
    'Product strategy & focus',
    'Scaling operations',
    'Co-founder & team dynamics',
];

const timeCommitmentOptions = [
    '2 hours/month (session only)',
    '3–4 hours/month (session + async exchange)',
    '5+ hours/month (deep peer involvement)',
];

const initialFormData = {
    // Step 1: Founder & company
    founderName: '',
    roleTitle: '',
    workEmail: '',
    phone: '',
    linkedIn: '',
    cityCountry: '',
    companyName: '',
    website: '',
    yearFounded: '',
    sector: '',
    oneLineDescription: '',

    // Step 2: Stage & traction
    currentStage: 'Growth (Series A/B)',
    businessModel: '',
    monthlyRevenue: '',
    teamSize: '',
    capitalRaised: '',
    growthRate: '',
    currentPriorities: '',
    biggestChallenge: '',

    // Step 3: Preferences & trust
    selectedFocusAreas: ['Hiring & executive leadership', 'Fundraising & board dynamics'],
    selectedTimeCommitment: '3–4 hours/month (session + async exchange)',
    peerStagePreference: 'Similar stage (peers facing identical decisions)',
    avoidCompetitors: '',
    agreedConfidentiality: false,
    agreedCandour: false,
    agreedNoSelling: false,

    // Step 4: Final Declaration
    agreedDeclaration: false,
};

const FounderCirclesApplication = () => {
    const [currentStep, setCurrentStep] = useState(1);
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');
    const [submittingStatus, setSubmittingStatus] = useState('');

    // Form State
    const [formData, setFormData] = useState(initialFormData);

    const handleChange = (field, value) => {
        // Clear error for the field as user types
        if (errors[field]) {
            setErrors((prev) => ({ ...prev, [field]: null }));
        }
        setErrorMsg('');
        setFormData((prev) => ({ ...prev, [field]: value }));
    };

    const toggleFocusArea = (area) => {
        if (errors.selectedFocusAreas) {
            setErrors((prev) => ({ ...prev, selectedFocusAreas: null }));
        }
        setFormData((prev) => {
            const exists = prev.selectedFocusAreas.includes(area);
            return {
                ...prev,
                selectedFocusAreas: exists
                    ? prev.selectedFocusAreas.filter((item) => item !== area)
                    : [...prev.selectedFocusAreas, area],
            };
        });
    };

    // Step Validation on Continue Click using centralized utils/validators
    const validateStep = (step) => {
        const newErrors = {};

        if (step === 1) {
            const founderNameErr = validateRequired(formData.founderName, 'Full name', 2);
            if (founderNameErr) newErrors.founderName = founderNameErr;

            const roleTitleErr = validateRequired(formData.roleTitle, 'Role / Title', 2);
            if (roleTitleErr) newErrors.roleTitle = roleTitleErr;

            const emailErr = validateEmail(formData.workEmail, 'Work email', true);
            if (emailErr) newErrors.workEmail = emailErr;

            if (formData.phone) {
                const phoneErr = validatePhone(formData.phone, 'Phone number', false);
                if (phoneErr) newErrors.phone = phoneErr;
            }

            if (formData.linkedIn) {
                const linkedInErr = validateUrl(formData.linkedIn, 'LinkedIn profile URL', false);
                if (linkedInErr) newErrors.linkedIn = linkedInErr;
            }

            const cityCountryErr = validateRequired(formData.cityCountry, 'City & Country', 2);
            if (cityCountryErr) newErrors.cityCountry = cityCountryErr;

            const companyNameErr = validateRequired(formData.companyName, 'Company name', 2);
            if (companyNameErr) newErrors.companyName = companyNameErr;

            if (formData.website) {
                const websiteErr = validateUrl(formData.website, 'Website URL', false);
                if (websiteErr) newErrors.website = websiteErr;
            }

            if (formData.yearFounded) {
                const yearIntErr = validateInteger(formData.yearFounded, 'Year founded', false);
                if (yearIntErr || !/^(19\d\d|20\d\d)$/.test(String(formData.yearFounded).trim())) {
                    newErrors.yearFounded = 'Please enter a valid 4-digit year (e.g. 2023)';
                }
            }

            const sectorErr = validateSelect(formData.sector, 'a sector', true);
            if (sectorErr) newErrors.sector = sectorErr;

            const descErr = validateRequired(formData.oneLineDescription, 'One-line company description', 5);
            if (descErr) newErrors.oneLineDescription = descErr;
        }

        if (step === 2) {
            const stageErr = validateSelect(formData.currentStage, 'a company stage', true);
            if (stageErr) newErrors.currentStage = stageErr;

            const bmErr = validateSelect(formData.businessModel, 'a business model', true);
            if (bmErr) newErrors.businessModel = bmErr;

            if (formData.monthlyRevenue) {
                const revErr = validateNumeric(formData.monthlyRevenue, 'Monthly revenue', false);
                if (revErr) newErrors.monthlyRevenue = revErr;
            }

            if (formData.teamSize) {
                const teamErr = validateInteger(formData.teamSize, 'Team size', false);
                if (teamErr) newErrors.teamSize = teamErr;
            }

            if (formData.capitalRaised) {
                const capErr = validateNumeric(formData.capitalRaised, 'Total capital raised', false);
                if (capErr) newErrors.capitalRaised = capErr;
            }

            if (formData.growthRate) {
                const growthErr = validatePercentage(formData.growthRate, 'Growth rate', -100, 1000, false);
                if (growthErr) newErrors.growthRate = growthErr;
            }

            const prioritiesErr = validateRequired(formData.currentPriorities, 'Top priorities', 5);
            if (prioritiesErr) newErrors.currentPriorities = prioritiesErr;

            const challengeErr = validateRequired(formData.biggestChallenge, 'Core challenge', 5);
            if (challengeErr) newErrors.biggestChallenge = challengeErr;
        }

        if (step === 3) {
            if (!formData.selectedFocusAreas || formData.selectedFocusAreas.length === 0) {
                newErrors.selectedFocusAreas = 'Please select at least one focus area';
            }

            const allAgreed = formData.agreedConfidentiality && formData.agreedCandour && formData.agreedNoSelling;
            const agreementsErr = validateBoolean(allAgreed, 'You must accept all three circle commitments before continuing');
            if (agreementsErr) newErrors.agreements = agreementsErr;
        }

        if (step === 4) {
            const declErr = validateBoolean(formData.agreedDeclaration, 'Please confirm the declaration before submitting');
            if (declErr) newErrors.agreedDeclaration = declErr;
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleNext = async (e) => {
        e.preventDefault();
        if (validateStep(currentStep)) {
            if (currentStep < 4) {
                setCurrentStep((prev) => prev + 1);
                setErrorMsg('');
                window.scrollTo({ top: 0, behavior: 'smooth' });
            } else {
                // Final submission
                setLoading(true);
                setSubmittingStatus('Submitting your Founder Circles application...');
                setErrorMsg('');
                try {
                    const response = await axios.post(POST_FOUNDERS_CIRCLE_DATA, formData);
                    if (response.status === 201 || response.status === 200) {
                        setSubmitted(true);
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                    }
                } catch (error) {
                    console.error('Error submitting Founder Circles application:', error);
                    if (error.response && error.response.data) {
                        const details = error.response.data.details;
                        if (Array.isArray(details)) {
                            setErrorMsg(details.join('. '));
                        } else if (typeof details === 'string') {
                            setErrorMsg(details);
                        } else if (error.response.data.error) {
                            setErrorMsg(error.response.data.error);
                        } else {
                            setErrorMsg('An error occurred while submitting your application. Please try again.');
                        }
                    } else {
                        setErrorMsg(error.message || 'Network error. Please check your connection and try again.');
                    }
                } finally {
                    setLoading(false);
                    setSubmittingStatus('');
                }
            }
        }
    };

    const handleBack = () => {
        if (currentStep > 1) {
            setCurrentStep((prev) => prev - 1);
            setErrorMsg('');
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    const handleReset = () => {
        setFormData(initialFormData);
        setCurrentStep(1);
        setSubmitted(false);
        setErrorMsg('');
        setErrors({});
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const progressPercent = steps[currentStep - 1].percent;

    return (
        <div className="fca-page">
            {/* 1. Top Title and Stepper Progress */}
            <section className="fca-header-section">
                <div className="fca-tag">FOUNDER CIRCLES APPLICATION</div>
                <h1 className="fca-main-title">
                    {currentStep === 1 && 'Find your circle.'}
                    {currentStep === 2 && 'What stage are you building at?'}
                    {currentStep === 3 && 'What do you want from your circle?'}
                    {currentStep === 4 && 'Review your application.'}
                </h1>
                <p className="fca-main-desc">
                    {currentStep === 1 && 'Tell us who you are and what you are building. Every circle is deliberately composed around stage, role and operating context.'}
                    {currentStep === 2 && 'We match founders by stage, velocity and operating challenge. Honest context ensures conversations are immediately relevant.'}
                    {currentStep === 3 && 'Circles are built on mutual contribution, strict confidentiality and intentional proximity. Tell us what matters most.'}
                    {currentStep === 4 && 'Check that every detail represents where you are today. We review every application personally before suggesting a match.'}
                </p>

                {/* Dynamic Progress Stepper Bar */}
                <div className="fca-stepper-wrapper">
                    <div className="fca-stepper-track">
                        <div
                            className="fca-stepper-fill"
                            style={{ width: `${progressPercent}%` }}
                        ></div>
                        {steps.map((s) => {
                            const isActive = currentStep === s.id;
                            const isDone = currentStep > s.id;
                            return (
                                <div
                                    key={s.id}
                                    className={`fca-step-node ${isActive ? 'active' : ''} ${isDone ? 'completed' : ''}`}
                                    onClick={() => s.id < currentStep && setCurrentStep(s.id)}
                                >
                                    <div className="fca-node-dot">
                                        {isDone ? '✓' : `0${s.id}`}
                                    </div>
                                    <div className="fca-node-labels">
                                        <span className="fca-node-name">{s.label}</span>
                                        {isActive && <span className="fca-node-status">{s.percent}% complete</span>}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* 3. Main Split Container */}
            <main className="fca-container">
                {/* Form Card (Left) */}
                <section className="fca-form-card">
                    {submitted ? (
                        <div className="fca-success-card">
                            <div className="fca-success-icon-badge">✓</div>
                            <h2 className="fca-success-title">Application Submitted</h2>
                            <p className="fca-success-subtitle">
                                Thank you, <strong>{formData.founderName}</strong>. Your Founder Circles application for <strong>{formData.companyName}</strong> has been received for confidential review.
                            </p>
                            <div className="fca-success-summary">
                                <div className="fca-success-row">
                                    <span className="fca-success-row-label">Founder & Role</span>
                                    <span className="fca-success-row-value">{formData.founderName} ({formData.roleTitle})</span>
                                </div>
                                <div className="fca-success-row">
                                    <span className="fca-success-row-label">Work Email</span>
                                    <span className="fca-success-row-value">{formData.workEmail}</span>
                                </div>
                                <div className="fca-success-row">
                                    <span className="fca-success-row-label">Company & Sector</span>
                                    <span className="fca-success-row-value">{formData.companyName} · {formData.sector}</span>
                                </div>
                                <div className="fca-success-row">
                                    <span className="fca-success-row-label">Stage & Model</span>
                                    <span className="fca-success-row-value">{formData.currentStage} · {formData.businessModel}</span>
                                </div>
                                <div className="fca-success-row">
                                    <span className="fca-success-row-label">Location</span>
                                    <span className="fca-success-row-value">{formData.cityCountry}</span>
                                </div>
                                <div className="fca-success-row">
                                    <span className="fca-success-row-label">Focus Areas</span>
                                    <span className="fca-success-row-value">{formData.selectedFocusAreas.join(', ')}</span>
                                </div>
                                <div className="fca-success-row">
                                    <span className="fca-success-row-label">Time Commitment</span>
                                    <span className="fca-success-row-value">{formData.selectedTimeCommitment}</span>
                                </div>
                            </div>
                            <p className="fca-success-note">
                                Our admissions team reviews applications every Monday. We will contact you at <strong>{formData.workEmail}</strong> regarding circle cohort matches within 5 business days.
                            </p>
                            <button
                                type="button"
                                className="fca-btn-submit-another"
                                onClick={handleReset}
                            >
                                Submit Another Application
                            </button>
                        </div>
                    ) : (
                    <form onSubmit={handleNext}>
                        {errorMsg && (
                            <div className="fca-alert-error">
                                <span>⚠️</span>
                                <span>{errorMsg}</span>
                            </div>
                        )}
                        {/* ----------------- STEP 1 ----------------- */}
                        {currentStep === 1 && (
                            <div className="fca-step-content">
                                <div className="fca-step-heading-group">
                                    <h2>Founder & company profile</h2>
                                    <p>Start with the essentials</p>
                                </div>

                                <div className="fca-grid-2">
                                    <div className="fca-input-field">
                                        <label>Full name *</label>
                                        <input
                                            type="text"
                                            placeholder="Your name"
                                            value={formData.founderName}
                                            onChange={(e) => handleChange('founderName', e.target.value)}
                                        />
                                        {errors.founderName && <span className="fca-err">{errors.founderName}</span>}
                                    </div>

                                    <div className="fca-input-field">
                                        <label>Role / Title *</label>
                                        <input
                                            type="text"
                                            placeholder="e.g. Founder & CEO, Co-founder & CTO"
                                            value={formData.roleTitle}
                                            onChange={(e) => handleChange('roleTitle', e.target.value)}
                                        />
                                        {errors.roleTitle && <span className="fca-err">{errors.roleTitle}</span>}
                                    </div>

                                    <div className="fca-input-field">
                                        <label>Work email *</label>
                                        <input
                                            type="email"
                                            placeholder="you@company.com"
                                            value={formData.workEmail}
                                            onChange={(e) => handleChange('workEmail', e.target.value)}
                                        />
                                        {errors.workEmail && <span className="fca-err">{errors.workEmail}</span>}
                                    </div>

                                    <div className="fca-input-field">
                                        <label>Phone / WhatsApp (optional)</label>
                                        <input
                                            type="tel"
                                            placeholder="+1 (555) 000-0000"
                                            value={formData.phone}
                                            onChange={(e) => handleChange('phone', e.target.value)}
                                        />
                                        {errors.phone && <span className="fca-err">{errors.phone}</span>}
                                    </div>

                                    <div className="fca-input-field">
                                        <label>LinkedIn profile (optional)</label>
                                        <input
                                            type="text"
                                            placeholder="linkedin.com/in/..."
                                            value={formData.linkedIn}
                                            onChange={(e) => handleChange('linkedIn', e.target.value)}
                                        />
                                        {errors.linkedIn && <span className="fca-err">{errors.linkedIn}</span>}
                                    </div>

                                    <div className="fca-input-field">
                                        <label>City & Country *</label>
                                        <input
                                            type="text"
                                            placeholder="e.g. London, UK; Bengaluru, India"
                                            value={formData.cityCountry}
                                            onChange={(e) => handleChange('cityCountry', e.target.value)}
                                        />
                                        {errors.cityCountry && <span className="fca-err">{errors.cityCountry}</span>}
                                    </div>

                                    <div className="fca-input-field">
                                        <label>Company name *</label>
                                        <input
                                            type="text"
                                            placeholder="Your company"
                                            value={formData.companyName}
                                            onChange={(e) => handleChange('companyName', e.target.value)}
                                        />
                                        {errors.companyName && <span className="fca-err">{errors.companyName}</span>}
                                    </div>

                                    <div className="fca-input-field">
                                        <label>Website (optional)</label>
                                        <input
                                            type="text"
                                            placeholder="company.com"
                                            value={formData.website}
                                            onChange={(e) => handleChange('website', e.target.value)}
                                        />
                                        {errors.website && <span className="fca-err">{errors.website}</span>}
                                    </div>

                                    <div className="fca-input-field">
                                        <label>Year founded (Integer format)</label>
                                        <input
                                            type="text"
                                            placeholder="YYYY (e.g. 2023)"
                                            value={formData.yearFounded}
                                            onChange={(e) => handleChange('yearFounded', e.target.value)}
                                        />
                                        {errors.yearFounded && <span className="fca-err">{errors.yearFounded}</span>}
                                    </div>

                                    <div className="fca-input-field">
                                        <label>Sector *</label>
                                        <select
                                            value={formData.sector}
                                            onChange={(e) => handleChange('sector', e.target.value)}
                                        >
                                            <option value="">Select sector</option>
                                            <option value="B2B SaaS">B2B SaaS</option>
                                            <option value="Fintech">Fintech</option>
                                            <option value="Healthcare">Healthcare</option>
                                            <option value="Consumer Tech">Consumer Tech</option>
                                            <option value="Enterprise / AI">Enterprise / AI</option>
                                            <option value="Other">Other</option>
                                        </select>
                                        {errors.sector && <span className="fca-err">{errors.sector}</span>}
                                    </div>

                                    <div className="fca-input-field full-width">
                                        <label>One-line company description *</label>
                                        <textarea
                                            rows={3}
                                            placeholder="What does your company do, for whom, and what is your core advantage?"
                                            value={formData.oneLineDescription}
                                            onChange={(e) => handleChange('oneLineDescription', e.target.value)}
                                        ></textarea>
                                        {errors.oneLineDescription && <span className="fca-err">{errors.oneLineDescription}</span>}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* ----------------- STEP 2 ----------------- */}
                        {currentStep === 2 && (
                            <div className="fca-step-content">
                                <div className="fca-step-heading-group">
                                    <h2>Stage, scale & priorities</h2>
                                    <p>Help us place you in the right room</p>
                                </div>

                                <div className="fca-input-field full-width">
                                    <label>Current company stage *</label>
                                    <div className="fca-radio-cards-row">
                                        {['Pre-seed / Ideation', 'Early traction (Seed)', 'Growth (Series A/B)', 'Scale / Institution'].map((stg) => (
                                            <div
                                                key={stg}
                                                className={`fca-radio-card ${formData.currentStage === stg ? 'selected' : ''}`}
                                                onClick={() => handleChange('currentStage', stg)}
                                            >
                                                <span className="fca-rc-dot"></span>
                                                <span>{stg}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <div className="fca-grid-2" style={{ marginTop: '20px' }}>
                                    <div className="fca-input-field">
                                        <label>Business model *</label>
                                        <select
                                            value={formData.businessModel}
                                            onChange={(e) => handleChange('businessModel', e.target.value)}
                                        >
                                            <option value="">Select business model</option>
                                            <option value="B2B Subscription">B2B Subscription</option>
                                            <option value="Transactional / Marketplace">Transactional / Marketplace</option>
                                            <option value="Usage-based">Usage-based</option>
                                            <option value="Enterprise contract">Enterprise contract</option>
                                            <option value="Consumer D2C">Consumer D2C</option>
                                        </select>
                                        {errors.businessModel && <span className="fca-err">{errors.businessModel}</span>}
                                    </div>

                                    <div className="fca-input-field">
                                        <label>Monthly revenue (USD integer only)</label>
                                        <input
                                            type="text"
                                            placeholder="e.g. 35000"
                                            value={formData.monthlyRevenue}
                                            onChange={(e) => handleChange('monthlyRevenue', e.target.value)}
                                        />
                                        {errors.monthlyRevenue && <span className="fca-err">{errors.monthlyRevenue}</span>}
                                    </div>

                                    <div className="fca-input-field">
                                        <label>Full-time team size (Integer only)</label>
                                        <input
                                            type="text"
                                            placeholder="e.g. 14"
                                            value={formData.teamSize}
                                            onChange={(e) => handleChange('teamSize', e.target.value)}
                                        />
                                        {errors.teamSize && <span className="fca-err">{errors.teamSize}</span>}
                                    </div>

                                    <div className="fca-input-field">
                                        <label>Total capital raised (USD integer only)</label>
                                        <input
                                            type="text"
                                            placeholder="e.g. 1500000"
                                            value={formData.capitalRaised}
                                            onChange={(e) => handleChange('capitalRaised', e.target.value)}
                                        />
                                        {errors.capitalRaised && <span className="fca-err">{errors.capitalRaised}</span>}
                                    </div>

                                    <div className="fca-input-field full-width">
                                        <label>Annual or quarterly growth rate</label>
                                        <input
                                            type="text"
                                            placeholder="e.g. 120% YoY, 15% MoM"
                                            value={formData.growthRate}
                                            onChange={(e) => handleChange('growthRate', e.target.value)}
                                        />
                                        {errors.growthRate && <span className="fca-err">{errors.growthRate}</span>}
                                    </div>

                                    <div className="fca-input-field full-width">
                                        <label>Top priorities for the next 90 days *</label>
                                        <textarea
                                            rows={3}
                                            placeholder="What 2–3 milestones or decisions must happen in the next quarter?"
                                            value={formData.currentPriorities}
                                            onChange={(e) => handleChange('currentPriorities', e.target.value)}
                                        ></textarea>
                                        {errors.currentPriorities && <span className="fca-err">{errors.currentPriorities}</span>}
                                    </div>

                                    <div className="fca-input-field full-width">
                                        <label>Biggest operating challenge right now *</label>
                                        <textarea
                                            rows={3}
                                            placeholder="Where are you stuck? What decision keeps you up at night?"
                                            value={formData.biggestChallenge}
                                            onChange={(e) => handleChange('biggestChallenge', e.target.value)}
                                        ></textarea>
                                        {errors.biggestChallenge && <span className="fca-err">{errors.biggestChallenge}</span>}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* ----------------- STEP 3 ----------------- */}
                        {currentStep === 3 && (
                            <div className="fca-step-content">
                                <div className="fca-step-heading-group">
                                    <h2>Circle preferences & trust</h2>
                                    <p>Help us curate a group that adds real value</p>
                                </div>

                                <div className="fca-input-field full-width">
                                    <label>Focus areas you care most about discussing *</label>
                                    <div className="fca-pills-matrix">
                                        {focusAreas.map((area) => {
                                            const selected = formData.selectedFocusAreas.includes(area);
                                            return (
                                                <button
                                                    type="button"
                                                    key={area}
                                                    className={`fca-tag-button ${selected ? 'selected' : ''}`}
                                                    onClick={() => toggleFocusArea(area)}
                                                >
                                                    {area}
                                                </button>
                                            );
                                        })}
                                    </div>
                                    {errors.selectedFocusAreas && <span className="fca-err">{errors.selectedFocusAreas}</span>}
                                </div>

                                <div className="fca-input-field full-width" style={{ marginTop: '24px' }}>
                                    <label>Monthly time commitment</label>
                                    <div className="fca-radio-cards-row">
                                        {timeCommitmentOptions.map((opt) => (
                                            <div
                                                key={opt}
                                                className={`fca-radio-card ${formData.selectedTimeCommitment === opt ? 'selected' : ''}`}
                                                onClick={() => handleChange('selectedTimeCommitment', opt)}
                                            >
                                                <span className="fca-rc-dot"></span>
                                                <span>{opt}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <div className="fca-input-field full-width" style={{ marginTop: '20px' }}>
                                    <label>Companies or competitors to avoid matching with</label>
                                    <input
                                        type="text"
                                        placeholder="List any direct competitors or conflicts (we screen strictly)"
                                        value={formData.avoidCompetitors}
                                        onChange={(e) => handleChange('avoidCompetitors', e.target.value)}
                                    />
                                </div>

                                {/* Circle Agreement Declarations */}
                                <div className="fca-agreements-box">
                                    <h4>The Circle Agreement</h4>
                                    <p>Membership requires an unwavering commitment to these three principles:</p>

                                    <div className="fca-check-row">
                                        <input
                                            type="checkbox"
                                            id="conf"
                                            checked={formData.agreedConfidentiality}
                                            onChange={(e) => handleChange('agreedConfidentiality', e.target.checked)}
                                        />
                                        <label htmlFor="conf">
                                            <strong>Strict confidentiality:</strong> What is spoken in the circle stays in the circle. No leaks, no sharing.
                                        </label>
                                    </div>

                                    <div className="fca-check-row">
                                        <input
                                            type="checkbox"
                                            id="candour"
                                            checked={formData.agreedCandour}
                                            onChange={(e) => handleChange('agreedCandour', e.target.checked)}
                                        />
                                        <label htmlFor="candour">
                                            <strong>Radical candour & authenticity:</strong> Real problems only. No polished PR updates or investor vanity metrics.
                                        </label>
                                    </div>

                                    <div className="fca-check-row">
                                        <input
                                            type="checkbox"
                                            id="nosell"
                                            checked={formData.agreedNoSelling}
                                            onChange={(e) => handleChange('agreedNoSelling', e.target.checked)}
                                        />
                                        <label htmlFor="nosell">
                                            <strong>No selling or pitching:</strong> Circles are for mutual founder support, not pipeline prospecting or recruitment.
                                        </label>
                                    </div>
                                    {errors.agreements && <span className="fca-err">{errors.agreements}</span>}
                                </div>
                            </div>
                        )}

                        {/* ----------------- STEP 4 ----------------- */}
                        {currentStep === 4 && (
                            <div className="fca-step-content">
                                <div className="fca-step-heading-group">
                                    <h2>Review your application</h2>
                                    <p>Confirm your details before confidential review</p>
                                </div>

                                <div className="fca-review-summary-grid">
                                    <div className="fca-review-box">
                                        <div className="fca-rb-head">
                                            <strong>Founder & Company</strong>
                                            <button type="button" onClick={() => setCurrentStep(1)}>Edit</button>
                                        </div>
                                        <h4>{formData.founderName || 'Founder Name'}</h4>
                                        <p>{formData.roleTitle || 'Founder & CEO'} · {formData.companyName || 'Company'}</p>
                                        <span className="fca-rb-sub">{formData.cityCountry || 'City, Country'}</span>
                                    </div>

                                    <div className="fca-review-box">
                                        <div className="fca-rb-head">
                                            <strong>Stage & Scale</strong>
                                            <button type="button" onClick={() => setCurrentStep(2)}>Edit</button>
                                        </div>
                                        <h4>{formData.currentStage}</h4>
                                        <p>Team size: {formData.teamSize || '10+'} · MRR: ${formData.monthlyRevenue || '0'}</p>
                                        <span className="fca-rb-sub">{formData.businessModel || 'B2B SaaS'}</span>
                                    </div>

                                    <div className="fca-review-box full-span">
                                        <div className="fca-rb-head">
                                            <strong>Focus Priorities</strong>
                                            <button type="button" onClick={() => setCurrentStep(3)}>Edit</button>
                                        </div>
                                        <div className="fca-review-tags">
                                            {formData.selectedFocusAreas.map((fa) => (
                                                <span key={fa} className="fca-rev-pill">{fa}</span>
                                            ))}
                                        </div>
                                        <p style={{ marginTop: '10px' }}>Commitment: {formData.selectedTimeCommitment}</p>
                                    </div>
                                </div>

                                <div className="fca-final-declaration-wrap">
                                    <input
                                        type="checkbox"
                                        id="decl"
                                        checked={formData.agreedDeclaration}
                                        onChange={(e) => handleChange('agreedDeclaration', e.target.checked)}
                                    />
                                    <label htmlFor="decl">
                                        I confirm that the information provided is accurate and agree to participate in good faith if matched with a Founder Circle cohort.
                                    </label>
                                </div>
                                {errors.agreedDeclaration && <span className="fca-err">{errors.agreedDeclaration}</span>}
                            </div>
                        )}

                        {/* Bottom Actions Row */}
                        <div className="fca-actions-row">
                            {currentStep > 1 ? (
                                <button type="button" className="fca-btn-back" onClick={handleBack} disabled={loading}>
                                    ← Back
                                </button>
                            ) : (
                                <button type="button" className="fca-btn-link" onClick={() => alert('Draft saved')}>
                                    Save draft
                                </button>
                            )}

                            <div className="fca-actions-right">
                                <span className="fca-step-indicator">
                                    Step {currentStep} of {steps.length}
                                </span>
                                <button type="submit" className="fca-btn-submit" disabled={loading}>
                                    {loading
                                        ? (submittingStatus || 'Submitting...')
                                        : currentStep === 4
                                        ? 'Submit application 🚀'
                                        : 'Continue →'}
                                </button>
                            </div>
                        </div>

                        <div className="fca-privacy-note">
                            🛡️ Submitting an application does not guarantee placement. Your data is handled under strict confidentiality.
                        </div>
                    </form>
                    )}
                </section>

                {/* Right Dark Context Sidebar */}
                <aside className="fca-sidebar-card">
                    <div className="fca-sb-tag">CURATED MATCHING</div>
                    <h3 className="fca-sb-title">Why circles matter.</h3>
                    <p className="fca-sb-desc">
                        Circles are stage-matched, non-competing peer groups that meet monthly under strict confidentiality.
                    </p>

                    <div className="fca-sb-feature-list">
                        <div className="fca-sb-item">
                            <span className="fca-sbi-icon">👥</span>
                            <div>
                                <strong>6–8 founders only</strong>
                                <p>Small enough for real vulnerability, large enough for diverse insight.</p>
                            </div>
                        </div>

                        <div className="fca-sb-item">
                            <span className="fca-sbi-icon">🛡️</span>
                            <div>
                                <strong>Screened for conflicts</strong>
                                <p>No direct competitors or commercial conflicts in the room.</p>
                            </div>
                        </div>

                        <div className="fca-sb-item">
                            <span className="fca-sbi-icon">⏱️</span>
                            <div>
                                <strong>Facilitated sessions</strong>
                                <p>Structured 75-minute agendas focused on action, not casual networking.</p>
                            </div>
                        </div>
                    </div>

                    <div className="fca-sb-stats-box">
                        <div>
                            <span className="fca-stat-val">0{currentStep} / 04</span>
                            <span className="fca-stat-lbl">CURRENT STEP</span>
                        </div>
                        <div>
                            <span className="fca-stat-val">{progressPercent}%</span>
                            <span className="fca-stat-lbl">COMPLETED</span>
                        </div>
                    </div>

                    <div className="fca-sb-footer-note">
                        Applications are reviewed every Monday. Fit conversations are scheduled within 5 business days.
                    </div>
                </aside>
            </main>
        </div>
    );
};

export default FounderCirclesApplication;